import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { AuthProvider } from './context/AuthContext';
import { TenantProvider } from './context/TenantContext';
import PrivateRoute from './components/Common/PrivateRoute';
import Navbar from './components/Layout/Navbar';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import Dashboard from './components/Dashboard/Dashboard';
import ChartOfAccounts from './components/Accounting/ChartOfAccountsPro';
import JournalEntry from './components/Accounting/JournalEntry';
import Reports from './components/Accounting/Reports';
import AccountingLanding from './components/Accounting/AccountingLanding';
import AccountingPeriods from './components/Accounting/AccountingPeriods';
import TrialBalance from './components/Accounting/TrialBalance';
import BalanceSheet from './components/Accounting/BalanceSheet';
import ProfitLoss from './components/Accounting/ProfitLoss';
import GeneralLedger from './components/Accounting/GeneralLedger';
import Budgets from './components/Accounting/Budgets';
import Depreciation from './components/Accounting/Depreciation';
import BankReconciliation from './components/Accounting/BankReconciliation';
import FinanceHealth from './components/FinanceHealth/FinanceHealth';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: #f5f7fa;
    color: #333;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace;
  }

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  ::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
`;

const App: React.FC = () => {
  return (
    <>
      <GlobalStyle />
      <AuthProvider>
        <TenantProvider>
          <Router>
            <AppContainer>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />

                {/* Protected routes with Navbar */}
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <Dashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/finance-health"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <FinanceHealth />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <AccountingLanding />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting/periods"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <AccountingPeriods />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting/chart-of-accounts"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <ChartOfAccounts />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting/journal-entry"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <JournalEntry />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting/reports"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <Reports />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting/reports/trial-balance"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <TrialBalance />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting/reports/balance-sheet"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <BalanceSheet />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting/reports/profit-loss"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <ProfitLoss />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting/ledger"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <GeneralLedger />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting/budgets"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <Budgets />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting/depreciation"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <Depreciation />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting/reconciliation"
                  element={
                    <PrivateRoute>
                      <Navbar />
                      <BankReconciliation />
                    </PrivateRoute>
                  }
                />

                {/* Redirect root to dashboard */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                {/* 404 Not Found */}
                <Route
                  path="*"
                  element={
                    <NotFoundContainer>
                      <h1>404 - Page Not Found</h1>
                      <a href="/dashboard">Go to Dashboard</a>
                    </NotFoundContainer>
                  }
                />
              </Routes>
            </AppContainer>
          </Router>
        </TenantProvider>
      </AuthProvider>
    </>
  );
};

const NotFoundContainer = styled.div`
  text-align: center;
  padding: 60px;

  h1 {
    margin-bottom: 20px;
    color: #333;
  }

  a {
    color: #667eea;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export default App;
